import { createContext, useContext, useState, useRef, useEffect } from 'react';

const AudioContext = createContext(null);

// Free nursery rhyme from CDN
const BACKGROUND_MUSIC_URL = "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3";

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
};

export const AudioProvider = ({ children }) => {
  const [isMuted, setIsMuted] = useState(true); // Start muted by default
  const [volume, setVolume] = useState(0.3);
  const audioRef = useRef(null);

  useEffect(() => {
    // Create audio element
    const audio = new Audio(BACKGROUND_MUSIC_URL);
    audio.loop = true;
    audio.volume = volume;
    audioRef.current = audio;

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  const toggleMute = () => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.play().catch(console.error);
      } else {
        audioRef.current.pause();
      }
      setIsMuted(!isMuted);
    }
  };

  const playSound = (soundType) => {
    // Play different sound effects
    const sounds = {
      success: '/sounds/success.mp3',
      wrong: '/sounds/wrong.mp3',
      clap: '/sounds/clap.mp3',
    };
    
    // For now, we'll use the Web Audio API for simple sounds
    if (soundType === 'success') {
      playBeep(800, 200); // Higher pitch for success
    } else if (soundType === 'wrong') {
      playBeep(300, 300); // Lower pitch for wrong
    }
  };

  const playBeep = (frequency, duration) => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + duration / 1000);
      
      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + duration / 1000);
    } catch (e) {
      console.error('Audio error:', e);
    }
  };

  return (
    <AudioContext.Provider value={{ isMuted, toggleMute, volume, setVolume, playSound }}>
      {children}
    </AudioContext.Provider>
  );
};
